import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, Select, MenuItem, FormControl, Switch, FormControlLabel, TextField } from "@mui/material";
import countryList from "react-select-country-list";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import getSymbolFromCurrency from "currency-symbol-map";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { planService } from "../../../api";
import color from "../../../constants/color";
import styles from "./style";

const CreateNewClientPage = ({ loading, fetching = false, handleCreateClient, isEditMode = false, initialData }) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    companyId: "",
    adminId: "",
    plan: "",
    startDate: dayjs(),
    isActive: true,
    companyName: "",
    companyMailingAddress: "",
    address1: "",
    owner: "",
    phoneNumber: "",
    phoneCountryCode: "27",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    region: "",
    industry: "",
    billingCycle: "",
    adminEmail: "",
    adminFirstName: "",
    adminLastName: "",
    planPrice: "",
    planCurrency: "",
    planExpiryDate: null,
    moduleAccessOverrides: {},
    moduleLimitOverrides: {},
    usage: {},
    effectiveAccess: {},
    effectiveLimits: {},
    moduleAccess: {},
    moduleLimits: {},
  });
  const options = countryList().getData();

  const [errors, setErrors] = useState({});
  const [planList, setPlanList] = useState([]);
  const [initialFormData, setInitialFormData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const checkFormChanges = () => {
    if (!isEditMode) {
      return true;
    }

    if (!initialFormData) {
      return false;
    }

    const getDateString = (date) => {
      if (!date) return "";
      try {
        if (date && typeof date.format === "function") {
          return date.format("YYYY-MM-DD");
        }
        if (date instanceof Date) {
          return dayjs(date).format("YYYY-MM-DD");
        }
        return String(date);
      } catch (e) {
        return "";
      }
    };

    const currentDate = getDateString(formData.startDate);
    const initialDate = getDateString(initialFormData.startDate);
    const normalizeString = (val) => String(val || "").trim();

    return (
      normalizeString(formData.companyId) !== normalizeString(initialFormData.companyId) ||
      normalizeString(formData.adminId) !== normalizeString(initialFormData.adminId) ||
      normalizeString(formData.plan) !== normalizeString(initialFormData.plan) ||
      currentDate !== initialDate ||
      Boolean(formData.isActive) !== Boolean(initialFormData.isActive) ||
      normalizeString(formData.companyName) !== normalizeString(initialFormData.companyName) ||
      normalizeString(formData.companyMailingAddress) !== normalizeString(initialFormData.companyMailingAddress) ||
      normalizeString(formData.address1) !== normalizeString(initialFormData.address1) ||
      normalizeString(formData.owner) !== normalizeString(initialFormData.owner) ||
      normalizeString(formData.phoneNumber) !== normalizeString(initialFormData.phoneNumber) ||
      normalizeString(formData.phoneCountryCode) !== normalizeString(initialFormData.phoneCountryCode) ||
      normalizeString(formData.city) !== normalizeString(initialFormData.city) ||
      normalizeString(formData.state) !== normalizeString(initialFormData.state) ||
      normalizeString(formData.zipCode) !== normalizeString(initialFormData.zipCode) ||
      normalizeString(formData.country) !== normalizeString(initialFormData.country) ||
      normalizeString(formData.industry) !== normalizeString(initialFormData.industry) ||
      normalizeString(formData.adminEmail) !== normalizeString(initialFormData.adminEmail) ||
      getDateString(formData.planExpiryDate) !== getDateString(initialFormData.planExpiryDate) ||
      normalizeString(formData.planPrice) !== normalizeString(initialFormData.planPrice) ||
      JSON.stringify(formData.moduleAccessOverrides) !== JSON.stringify(initialFormData.moduleAccessOverrides) ||
      JSON.stringify(formData.moduleLimitOverrides) !== JSON.stringify(initialFormData.moduleLimitOverrides)
    );
  };

  useEffect(() => {
    if (isEditMode && initialData) {
      const normalizedStartDate = initialData.startDate
        ? (initialData.startDate.format ? initialData.startDate : dayjs(initialData.startDate))
        : dayjs();

      const dataCopy = {
        companyId: "",
        adminId: "",
        plan: "",
        isActive: true,
        ...initialData,
        startDate: normalizedStartDate,
        planExpiryDate: initialData.planExpiryDate ? (initialData.planExpiryDate.format ? initialData.planExpiryDate : dayjs(initialData.planExpiryDate)) : null,
      };
      setFormData(dataCopy);
      const initialCopy = {
        companyId: "",
        adminId: "",
        plan: "",
        isActive: true,
        ...initialData,
        startDate: normalizedStartDate,
      };
      setInitialFormData(initialCopy);
      setHasChanges(false);
    }
  }, [isEditMode, initialData]);

  useEffect(() => {
    if (isEditMode && initialFormData) {
      const changed = checkFormChanges();
      setHasChanges(changed);
    } else {
      setHasChanges(!isEditMode);
    }
  }, [formData, initialFormData, isEditMode]);

  const fetchPlans = async () => {
    try {
      const result = await planService.list();
      const normalizeListResponse = (response) => {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response?.data)) return response.data;
        if (Array.isArray(response?.result)) return response.result;
        return [];
      };
      const plans = normalizeListResponse(result);
      setPlanList(plans || []);
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    if (isEditMode && !hasChanges) {
      setHasChanges(true);
    }
  };

  const isFormValid = () => {
    return (
      formData.plan !== "" &&
      formData.startDate !== null
    );
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.plan) newErrors.plan = "Plan is required";
    if (!formData.startDate) newErrors.startDate = "Start date is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const planCurrencyCode = String(formData.planCurrency || "").toUpperCase();
  const planCurrencySymbol = planCurrencyCode ? getSymbolFromCurrency(planCurrencyCode) : "";
  const planPriceAdornment = planCurrencySymbol || planCurrencyCode || "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      await handleCreateClient(formData);
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerContainer}>
        <Typography sx={styles.title}>{isEditMode ? "Edit Client" : "Add New Client"}</Typography>
        <Typography sx={styles.subtitle}>{isEditMode ? "Update company subscription details." : "Create and configure a new company subscription."}</Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        {isEditMode && (
          <Box sx={styles.section}>
            <Typography sx={styles.sectionTitle}>Company Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Company Name"
                  value={formData.companyName}
                  onChange={(e) => handleChange("companyName", e.target.value)}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Owner Name"
                  value={formData.owner}
                  onChange={(e) => handleChange("owner", e.target.value)}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="email"
                  placeholder="Company Email Address"
                  value={formData.companyMailingAddress}
                  onChange={(e) => handleChange("companyMailingAddress", e.target.value)}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Address Line 1"
                  value={formData.address1}
                  onChange={(e) => handleChange("address1", e.target.value)}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ ...styles.phoneInputContainer, mb: 0 }}>
                  <PhoneInput
                    country={"za"}
                    enableSearch={true}
                    value={formData.phoneNumber}
                    onChange={(phone, countryData) => {
                      handleChange("phoneNumber", phone);
                      handleChange("phoneCountryCode", countryData?.dialCode || "27");
                    }}
                    inputProps={{ name: "phoneNumber" }}
                    containerStyle={{ width: "100%" }}
                    inputStyle={{
                      width: "100%",
                      height: "42px",
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      color: "#000",
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="City"
                  value={formData.city}
                  onChange={(e) => handleChange("city", e.target.value)}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="State"
                  value={formData.state}
                  onChange={(e) => handleChange("state", e.target.value)}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Zip Code"
                  value={formData.zipCode}
                  onChange={(e) => handleChange("zipCode", e.target.value)}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <Select
                    value={formData.country}
                    onChange={(e) => handleChange("country", e.target.value)}
                    displayEmpty
                    renderValue={(selected) =>
                      selected && selected !== "" ? selected : "Country"
                    }
                    sx={{ ...styles.select, backgroundColor: "#fff" }}
                  >
                    <MenuItem value="" disabled>Country</MenuItem>
                    {options.map((option, index) => (
                      <MenuItem key={index} value={option.label}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Region"
                  value={formData.region}
                  onChange={(e) => handleChange("region", e.target.value)}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Industry"
                  value={formData.industry}
                  onChange={(e) => handleChange("industry", e.target.value)}
                  sx={styles.input}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        <Box sx={styles.section}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
            <Typography sx={{ ...styles.sectionTitle, mb: 0 }}>Subscription Plan</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleChange("isActive", e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography sx={{ fontSize: "14px", color: color.text }}>
                  Active Subscription
                </Typography>
              }
              sx={{ m: 0 }}
            />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth error={!!errors.plan}>
                <Select
                  value={formData.plan}
                  onChange={(e) => handleChange("plan", e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) return "Select Plan";
                    const match = planList.find((plan) => (plan?._id || plan?.id) === selected);
                    return match?.planName ?? selected;
                  }}
                  sx={{
                    ...styles.select,
                    color: formData.plan ? "inherit" : "#9E9E9E",
                    "& .MuiSelect-icon": {
                      color: formData.plan ? "inherit" : "#9E9E9E",
                    },
                  }}
                >
                  <MenuItem value="" disabled>Select Plan</MenuItem>
                  {planList.map((plan) => {
                    const isActive = plan.isActive === true;
                    return (
                      <MenuItem
                        key={plan._id || plan.id}
                        value={plan._id || plan.id}
                        disabled={!isActive}
                        sx={!isActive ? { color: "#aaa" } : {}}
                      >
                        {plan.planName}
                        {!isActive && " (Inactive)"}
                      </MenuItem>
                    );
                  })}
                </Select>
                {errors.plan && (
                  <Typography sx={styles.errorText}>{errors.plan}</Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Activation Date"
                  value={formData.startDate}
                  onChange={(newValue) => handleChange("startDate", newValue)}
                  format="DD-MM-YYYY"
                  minDate={isEditMode ? undefined : dayjs()}
                  sx={styles.datePicker}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: "dd-mm-yyyy",
                      error: !!errors.startDate,
                      helperText: errors.startDate,
                      InputLabelProps: { shrink: true },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            {isEditMode && (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Price"
                  placeholder="Price"
                  value={formData.planPrice}
                  onChange={(e) => handleChange("planPrice", e.target.value)}
                  disabled={isEditMode}
                  sx={{ ...styles.input, ...(isEditMode ? { "& .MuiInputBase-root": { backgroundColor: "#f5f5f5" } } : {}) }}
                  InputProps={{
                    startAdornment: planPriceAdornment ? (
                      <Typography sx={{ mr: 1, fontSize: "14px" }}>{planPriceAdornment}</Typography>
                    ) : null,
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {isEditMode && (
              <Grid item xs={12} md={4}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                  value={formData.planExpiryDate}
                  onChange={(newValue) => handleChange("planExpiryDate", newValue)}
                  format="DD-MM-YYYY"
                  sx={styles.datePicker}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: "Plan Expiry Date",
                      InputLabelProps: { shrink: true },
                    },
                  }}
                  label="Plan Expiry Date"
                />
                </LocalizationProvider>
              </Grid>
            )}
            {isEditMode && (
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <Select
                    value={formData.billingCycle || ""}
                    displayEmpty
                    disabled
                    sx={{ ...styles.select, backgroundColor: "#f5f5f5" }}
                  >
                    <MenuItem value="" disabled>Billing Cycle</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                    <MenuItem value="quarterly">Quarterly</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </Box>

        {isEditMode && (
          <Box sx={styles.section}>
            <Typography sx={styles.sectionTitle}>Module Usage & Limits</Typography>
            {[
              { key: "companies", label: "Companies", hideToggle: true, limits: [{ key: "companiesPerPlan", label: "Limit" }] },
              { key: "operators", label: "Operators", limits: [{ key: "operatorsPerPlan", label: "Limit" }] },
              { key: "invoicing", label: "Invoicing", limits: [{ key: "invoicesPerPlan", label: "Limit" }, { key: "invoiceValueCap", label: "Value Cap" }] },
              { key: "manageEmployees", label: "Users", limits: [{ key: "activeEmployees", label: "Limit" }] },
              { key: "attendance", label: "Attendance", limits: [{ key: "attendanceDaysPerPlan", label: "Limit" }] },
              { key: "loans", label: "Loans", limits: [{ key: "activeLoans", label: "Limit" }] },
              { key: "deductions", label: "Deductions", limits: [{ key: "activeDeductions", label: "Limit" }] },
              { key: "leaveManagement", label: "Leave Management", limits: [{ key: "leaveRequestsPerPlan", label: "Limit" }] },
              { key: "commission", label: "Commission", limits: [{ key: "commissionRunsPerPlan", label: "Limit" }] },
              { key: "travelClaims", label: "Travel Claims & Allowances", limits: [{ key: "travelClaimsPerPlan", label: "Claims" }, { key: "allowancesPerPlan", label: "Allowances" }] },
              { key: "recruitment", label: "Recruitment", limits: [{ key: "recruitmentPerPlan", label: "Limit" }] },
              { key: "timesheet", label: "Timesheet", limits: [{ key: "timesheetPerPlan", label: "Limit" }] },
              { key: "projects", label: "Projects", limits: [{ key: "activeProjects", label: "Limit" }] },
              { key: "payroll", label: "Payroll", limits: [{ key: "payrollRunsPerPlan", label: "Limit" }] },
              { key: "salaryManagement", label: "Salary Management", limits: [{ key: "payslipLimit", label: "Limit" }] },
              { key: "reports", label: "Reports", limits: [
                { key: "reportDownloadsPerPlan", label: "Limit" },
                { key: "exportFormats", label: "Export formats", type: "select", options: ["PDF", "CSV", "Excel"] }
              ] },
              { key: "documentsUploadPerEmployee", label: "Document Uploads (Per Employee)", hideToggle: true, limits: [{ key: "employeeDocsPerEmployee", label: "Limit" }] },
            ].map((module) => {
              // hideToggle rows mirror the plan form: they have no access flag, so they are always on.
              const isEnabled = module.hideToggle || (formData.moduleAccessOverrides[module.key] ?? formData.moduleAccess[module.key] ?? formData.effectiveAccess[module.key] ?? false);

              return (
                <Box key={module.key} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1, borderBottom: "1px solid #f0f0f0" }}>
                  <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                    {module.hideToggle ? (
                      <Box sx={{ width: "40px" }} />
                    ) : (
                      <Switch
                        size="small"
                        checked={isEnabled}
                        onChange={(e) => {
                          const newOverrides = { ...formData.moduleAccessOverrides, [module.key]: e.target.checked };
                          handleChange("moduleAccessOverrides", newOverrides);
                        }}
                      />
                    )}
                    <Typography sx={{ ml: 1, fontSize: "15px", fontWeight: 500 }}>{module.label}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    {module.limits.map((limit) => {
                      const limitValue = formData.moduleLimitOverrides[limit.key] ?? formData.moduleLimits[limit.key] ?? formData.effectiveLimits[limit.key] ?? "";
                      const usedValue = formData.usage[limit.key]?.used ?? formData.usage[module.key]?.used ?? 0;

                      return (
                        <Box key={limit.key} sx={{ display: "flex", gap: 2 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", fontSize: "13px" }}>{limit.label}</Typography>
                            {limit.type === "select" ? (
                              <FormControl size="small" sx={{ width: "150px" }}>
                                <Select
                                  multiple
                                  value={Array.isArray(limitValue) ? limitValue : []}
                                  onChange={(e) => {
                                    const newLimits = { ...formData.moduleLimitOverrides, [limit.key]: e.target.value };
                                    handleChange("moduleLimitOverrides", newLimits);
                                  }}
                                  renderValue={(selected) => selected.join(", ")}
                                  sx={{ 
                                    "& .MuiSelect-select": { py: 0.5, fontSize: "14px" },
                                    borderRadius: "8px",
                                    backgroundColor: isEnabled ? "inherit" : "#f5f5f5"
                                  }}
                                  disabled={!isEnabled}
                                >
                                  {(limit.options || []).map((opt) => (
                                    <MenuItem key={opt} value={opt} sx={{ fontSize: "14px" }}>{opt}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              <TextField
                                size="small"
                                value={limitValue}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const newLimits = { ...formData.moduleLimitOverrides, [limit.key]: val === "" ? "" : Number(val) };
                                  handleChange("moduleLimitOverrides", newLimits);
                                }}
                                disabled={!isEnabled}
                                sx={{ width: "100px", "& .MuiInputBase-input": { py: 0.5, fontSize: "14px", backgroundColor: isEnabled ? "inherit" : "#f5f5f5" } }}
                              />
                            )}
                          </Box>
                          {limit.type !== "select" && (
                            <Box>
                              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", fontSize: "13px" }}>Used</Typography>
                              <TextField
                                size="small"
                                value={usedValue}
                                disabled
                                sx={{ width: "100px", "& .MuiInputBase-input": { py: 0.5, fontSize: "14px", backgroundColor: "#f9f9f9" } }}
                              />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {isEditMode && (
          <Box sx={styles.section}>
            <Typography sx={styles.sectionTitle}>Client Admin Login</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="email"
                  placeholder="Admin Email (Login Username)"
                  value={formData.adminEmail}
                  onChange={(e) => handleChange("adminEmail", e.target.value)}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: "14px", color: color.lightText }}>
                  These credentials are used by the client Super Admin to access the platform.
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        <Box sx={styles.buttonContainer}>
          <LoadingButton
            variant="contained"
            type="submit"
            loading={loading || fetching}
            disabled={!isFormValid() || fetching || (isEditMode && !hasChanges)}
            sx={styles.submitButton}
          >
            {isEditMode ? "Update Client" : "Create Client"}
          </LoadingButton>
          <LoadingButton
            variant="contained"
            onClick={() => navigate("/dashboard/userManagement")}
            sx={styles.cancelButton}
          >
            Cancel
          </LoadingButton>
        </Box>
      </form>
    </Box>
  );
};

export default CreateNewClientPage;
